/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import { partialMenuApi, PARTIAL_MENU_REFRESH_TAGS } from "queries"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import {
  useLazyFetchCatalogueQuery,
  useLazyFetchMenuQuery,
} from "queries/menuApi"
import {
  fetchCatalogueWithMigration,
  fetchMenuWithMigration,
} from "utils/rtk_migration_helper"

export const useFetchMenu = () => {
  const dispatch = useDispatch()
  const enablePartialMenu = useEnablePartialMenu()
  const {
    flags: {
      fetchCatalogue: isFetchCatalogueRtkEnabled,
      fetchMenu: isFetchMenuRtkEnabled,
    },
    isLoading: isFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()
  const [triggerFetchCatalogueRtk] = useLazyFetchCatalogueQuery()
  const [triggerFetchMenuRtk] = useLazyFetchMenuQuery()

  const {
    merchantID,
    isSellingTimeStructure,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
    isMartDepartmentStructure,
  } = useSelector((state: AppState) => state?.menu)

  const fetchMenu = useCallback(async () => {
    if (isMartDepartmentStructure) {
      if (enablePartialMenu) {
        return dispatch(
          partialMenuApi.util.invalidateTags(PARTIAL_MENU_REFRESH_TAGS),
        )
      }

      if (isFlagLoading) return Promise.resolve()

      return fetchCatalogueWithMigration({
        dispatch,
        dispatchLegacyFetch: () =>
          dispatch(
            actionCreator.menu.fetchCatalogue({
              merchantID,
              isSellingTimeStructure,
              merchantGroupID,
              menuGroupID,
            }),
          ),
        fetchCatalogueRtk: triggerFetchCatalogueRtk,
        isRtkFetchCatalogueEnabled: isFetchCatalogueRtkEnabled,
        isRefreshing: true,
        request: {
          merchantID,
          isSellingTimeStructure,
          merchantGroupID,
          menuGroupID,
        },
      })
    }

    if (isFlagLoading) {
      return undefined
    }

    return fetchMenuWithMigration({
      dispatch,
      merchantID,
      merchantGroupID,
      menuGroupID,
      isSellingTimeStructure,
      isRtkFetchMenuEnabled: isFetchMenuRtkEnabled,
      fetchMenuRtk: triggerFetchMenuRtk,
    })
  }, [
    dispatch,
    enablePartialMenu,
    isFetchCatalogueRtkEnabled,
    isFetchMenuRtkEnabled,
    isFlagLoading,
    isSellingTimeStructure,
    menuGroupID,
    merchantGroupID,
    merchantID,
    isMartDepartmentStructure,
    triggerFetchCatalogueRtk,
    triggerFetchMenuRtk,
  ])

  return { fetchMenu }
}
